import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Typography,
    Tooltip,
    Alert,
    Collapse,
    Chip,
    Snackbar,
    Switch
} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import MainCard from "../../../components/cards/MainCard";
import { useTheme } from "@mui/material/styles";
import { getCardColor } from "../../../utils/colorUtils";
import ConfirmationDialog from "../../../utils/confirmationDialog";
import TrimmedTextField from "../../../components/TrimmedTextField";
import {
    useGetIdentityStatusQuery,
    useCreateIdentityMutation,
    useCheckVerificationMutation,
    useRemoveIdentityMutation,
    useUpdateIdentityMutation
} from "../../../store/services/email-identity-service";

CustomEmailDomainSettings.propTypes = {
    tenancyId: PropTypes.string.isRequired
};

export default function CustomEmailDomainSettings({ tenancyId }) {
    const theme = useTheme();
    const lightMode = theme.palette.mode === "light";

    // RTK Query hooks
    const { data: identity, isLoading: loading, refetch } = useGetIdentityStatusQuery(
        { tenancyId: tenancyId },
        { skip: !tenancyId }
    );
    const [createIdentity, { isLoading: isCreating }] = useCreateIdentityMutation();
    const [checkVerification, { isLoading: isCheckingVerification }] = useCheckVerificationMutation();
    const [removeIdentity, { isLoading: isRemoving }] = useRemoveIdentityMutation();
    const [updateIdentity, { isLoading: isUpdating }] = useUpdateIdentityMutation();

    // State management
    const [parentDomain, setParentDomain] = useState("");
    const [subdomainPrefix, setSubdomainPrefix] = useState("training");
    const [localPart, setLocalPart] = useState("courses");
    const [showNsRecords, setShowNsRecords] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    // Error states
    const [parentDomainError, setParentDomainError] = useState("");
    const [subdomainPrefixError, setSubdomainPrefixError] = useState("");
    const [localPartError, setLocalPartError] = useState("");

    // Dialog state
    const [dialogOpen, setDialogOpen] = useState(false);
    const [familySharingDialogOpen, setFamilySharingDialogOpen] = useState(false);

    // Alert state
    const [alert, setAlert] = useState({ show: false, message: "", severity: "info" });

    // Family sharing state
    const [formData, setFormData] = useState({
        sharedTenancyIds: identity?.sharedTenancyIds || [],
        publishToFamily: identity?.publishToFamily || false
    });

    // Set NS records visibility based on identity status
    useEffect(() => {
        if (identity && identity.status !== "Verified") {
            setShowNsRecords(true);
        }
    }, [identity]);

    // Update formData when identity changes
    useEffect(() => {
        if (identity) {
            setFormData({
                sharedTenancyIds: identity.sharedTenancyIds || [],
                publishToFamily: identity.publishToFamily || false
            });
        }
    }, [identity]);

    const validateDomain = (domain) => {
        const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/;
        return domainRegex.test(domain.toLowerCase());
    };

    const validateLocalPart = (local) => {
        const localPartRegex = /^[a-z0-9._-]+$/;
        return localPartRegex.test(local.toLowerCase()) && local.length <= 64;
    };

    const validateSubdomainPrefix = (prefix) => {
        // Subdomain labels: alphanumeric and hyphens, but can't start or end with hyphen
        const subdomainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
        return subdomainRegex.test(prefix.toLowerCase()) && prefix.length <= 63;
    };

    const handleCreateIdentity = async () => {
        // Reset errors
        setParentDomainError("");
        setSubdomainPrefixError("");
        setLocalPartError("");

        // Validate inputs
        let hasError = false;

        if (!parentDomain.trim()) {
            setParentDomainError("Parent domain is required");
            hasError = true;
        } else if (!validateDomain(parentDomain.trim())) {
            setParentDomainError("Invalid domain format");
            hasError = true;
        }

        if (!subdomainPrefix.trim()) {
            setSubdomainPrefixError("Subdomain prefix is required");
            hasError = true;
        } else if (!validateSubdomainPrefix(subdomainPrefix.trim())) {
            setSubdomainPrefixError("Invalid subdomain format. Use only letters, numbers, and hyphens (can't start/end with hyphen)");
            hasError = true;
        }

        if (!localPart.trim()) {
            setLocalPartError("Email sender name is required");
            hasError = true;
        } else if (!validateLocalPart(localPart.trim())) {
            setLocalPartError("Only letters, numbers, dots, hyphens, and underscores allowed");
            hasError = true;
        }

        if (hasError) return;

        try {
            await createIdentity({
                tenancyId: tenancyId,
                parentDomain: parentDomain.trim().toLowerCase(),
                subdomainPrefix: subdomainPrefix.trim().toLowerCase(),
                localPart: localPart.trim().toLowerCase(),
                sharedTenancyIds: [],
                publishToFamily: formData.publishToFamily || false
            }).unwrap();

            setShowNsRecords(true);
            showAlert(
                "Custom domain created successfully! Please add the NS records to your DNS provider.",
                "success"
            );
        } catch (error) {
            console.error("Full error object:", error);

            const errorMessage = error?.data?.error || error?.error || "";

            // Handle 409 conflict errors
            if (error.status === 409) {
                // Just show the backend error message directly
                showAlert(errorMessage || "This configuration is already in use.", "error");
            } else {
                // Other errors
                showAlert(errorMessage || "Failed to create custom domain. Please try again.", "error");
            }
        }
    };

    const handleCheckVerification = async () => {
        try {
            const result = await checkVerification({ tenancyId: tenancyId }).unwrap();

            if (result.isVerified) {
                setShowNsRecords(false);
                showAlert(result.message, "success");
            } else {
                showAlert(result.message, "info");
            }
        } catch (error) {
            showAlert("Failed to check verification status", "error");
        }
    };

    const handleRemoveIdentity = async () => {
        setDialogOpen(false);

        try {
            const result = await removeIdentity({ tenancyId: tenancyId }).unwrap();

            // Reset form state immediately
            setParentDomain("");
            setSubdomainPrefix("training");
            setLocalPart("courses");
            setShowNsRecords(false);

            // Force refetch to get null result
            await refetch();

            showAlert("Custom domain removed successfully", "success");
        } catch (error) {
            showAlert(error.data?.error || "Failed to remove custom domain", "error");
        }
    };

    const handleToggleFamilySharing = async () => {
        setFamilySharingDialogOpen(false);

        const nextPublishToFamily = !identity?.publishToFamily;

        try {
            await updateIdentity({
                tenancyId: tenancyId,
                publishToFamily: nextPublishToFamily,
                sharedTenancyIds: identity?.sharedTenancyIds || []
            }).unwrap();

            await refetch();

            showAlert(
                nextPublishToFamily
                    ? "Family sharing enabled. Child tenancies without their own custom domain will now inherit this one."
                    : "Family sharing disabled. Child tenancies will no longer inherit this domain.",
                "success"
            );
        } catch (error) {
            showAlert(error?.data?.error || "Failed to update family sharing. Please try again.", "error");
        }
    };

    const handleCopyNsRecords = (index) => {
        if (identity?.nsRecords?.[0]?.values) {
            const nsRecordsText = identity.nsRecords[0].values.join("\n");
            navigator.clipboard.writeText(nsRecordsText);
            setCopiedIndex(index);
            setTimeout(() => setCopiedIndex(null), 2000);
        }
    };

    const showAlert = (message, severity) => {
        setAlert({ show: true, message, severity });
        setTimeout(() => setAlert({ show: false, message: "", severity: "info" }), 6000);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "Verified":
                return "success";
            case "Pending":
                return "default";
            default:
                return "default";
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case "Verified":
                return <CheckCircleIcon sx={{ fontSize: 20 }} />;
            case "Pending":
                return <ErrorOutlineIcon sx={{ fontSize: 20 }} />;
            default:
                return null;
        }
    };

    if (loading) {
        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                    <Typography variant="h4" sx={{ ml: 1 }}>
                        Custom Email Domain
                    </Typography>
                    <Tooltip title="Set up a custom subdomain to send training emails from your organisation's domain.">
                        <IconButton>
                            <HelpIcon sx={{ color: 'secondary.main' }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <MainCard content={true} boxShadow={true} border={false} sx={{ height: "80%", bgcolor: getCardColor(lightMode), pb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
                        <CircularProgress />
                    </Box>
                </MainCard>
            </>
        );
    }

    // If no identity exists, show create form
    if (!identity) {
        return (
            <>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                    <Typography variant="h4" sx={{ ml: 1 }}>
                        Custom Email Domain
                    </Typography>
                    <Tooltip title="Set up a custom subdomain to send training emails from your organisation's domain.">
                        <IconButton>
                            <HelpIcon sx={{ color: 'secondary.main' }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                <MainCard
                    content={true}
                    boxShadow={true}
                    border={false}
                    sx={{ mb: 3, bgcolor: getCardColor(lightMode), pb: 3 }}
                >
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                <Tooltip title="Enter your organisation's root domain (e.g., company.com, organisation.co.uk)">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        Your organisation's domain:
                                    </Typography>
                                    <TrimmedTextField
                                        fullWidth
                                        placeholder="examplecompany.com"
                                        value={parentDomain}
                                        onChange={(e) => {
                                            setParentDomain(e.target.value);
                                            setParentDomainError("");
                                        }}
                                        error={!!parentDomainError}
                                        helperText={parentDomainError}
                                        disabled={isCreating}
                                        allowNumbers={true}
                                        allowSpecialChars={['.', '-']}
                                        showFeedback={false}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                <Tooltip title="The subdomain prefix (e.g., 'training', 'courses', 'info'). This will be: subdomain.yourdomain.com">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        Subdomain prefix:
                                    </Typography>
                                    <TrimmedTextField
                                        fullWidth
                                        placeholder="training"
                                        value={subdomainPrefix}
                                        onChange={(e) => {
                                            setSubdomainPrefix(e.target.value);
                                            setSubdomainPrefixError("");
                                        }}
                                        error={!!subdomainPrefixError}
                                        helperText={subdomainPrefixError || "Examples: training, courses, info"}
                                        disabled={isCreating}
                                        allowNumbers={true}
                                        allowSpecialChars={['-']}
                                        showFeedback={false}
                                    />
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                                <Tooltip title="The local part of the email address (appears before @). Default is 'courses'">
                                    <IconButton size="small">
                                        <HelpIcon sx={{ color: "secondary.main" }} />
                                    </IconButton>
                                </Tooltip>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                                        Email sender name (local part):
                                    </Typography>
                                    <TrimmedTextField
                                        fullWidth
                                        placeholder="training"
                                        value={localPart}
                                        onChange={(e) => {
                                            setLocalPart(e.target.value);
                                            setLocalPartError("");
                                        }}
                                        error={!!localPartError}
                                        helperText={localPartError}
                                        disabled={isCreating}
                                        allowNumbers={true}
                                        allowSpecialChars={['.', '-', '_']}
                                        showFeedback={false}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={7}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mt: 2,
                                    bgcolor: getCardColor(lightMode),
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Tooltip
                                        title="When enabled, this domain will be accessible to all descendants of the selected tenancies"
                                        placement="top"
                                    >
                                        <IconButton size="small">
                                            <HelpIcon sx={{ color: 'secondary.main' }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Typography variant="body1">Share with Tenancy Family</Typography>
                                </Box>
                                <Switch
                                    checked={formData.publishToFamily || false}
                                    onChange={(e) =>
                                        setFormData({ ...formData, publishToFamily: e.target.checked })
                                    }
                                    disabled={isCreating}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <Alert severity="info" sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                    <strong>Note:</strong> If your parent tenancy has enabled family sharing, you automatically inherit their custom domain and don't need to create your own. Creating a domain here will override the inherited domain for this tenancy only. Family sharing can be turned on or off at any time after set-up.
                                </Typography>
                            </Alert>
                        </Grid>

                        <Grid item xs={12} md={7}>
                            {(parentDomain && localPart && subdomainPrefix) && (
                                <Box sx={{
                                    bgcolor: lightMode ? "grey.100" : "grey.800",
                                    p: 2,
                                    borderRadius: 1
                                }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                        Preview:
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                                        {localPart.toLowerCase()}@{subdomainPrefix.toLowerCase()}.{parentDomain.toLowerCase()}
                                    </Typography>
                                </Box>
                            )}
                        </Grid>

                        <Grid item xs={12} md={5}>
                            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", height: "100%" }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={handleCreateIdentity}
                                    disabled={isCreating || !parentDomain || !subdomainPrefix || !localPart}
                                    startIcon={isCreating ? <CircularProgress size={20} /> : null}
                                    sx={{ whiteSpace: 'nowrap' }}
                                >
                                    {isCreating ? "Creating..." : "Save & get NS records"}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </MainCard>

                <Snackbar
                    open={alert.show}
                    autoHideDuration={6000}
                    onClose={() => setAlert({ show: false, message: "", severity: "info" })}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setAlert({ show: false, message: "", severity: "info" })}
                        severity={alert.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {alert.message}
                    </Alert>
                </Snackbar>
            </>
        );
    }

    // If identity exists, show configuration status
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                <Typography variant="h4" sx={{ ml: 1 }}>
                    Custom Email Domain
                </Typography>
                <Tooltip title="Your custom email domain configuration">
                    <IconButton>
                        <HelpIcon sx={{ color: 'secondary.main' }} />
                    </IconButton>
                </Tooltip>
            </Box>
            <MainCard
                content={true}
                boxShadow={true}
                border={false}
                sx={{
                    height: "80%",
                    bgcolor: getCardColor(lightMode)
                }}
            >

                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Current domain:
                        </Typography>
                        <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                            {identity.domain}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 2
                        }}>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1
                            }}>
                                <Typography variant="body2" color="text.secondary">
                                    Status:
                                </Typography>
                                <Chip
                                    label={identity.status}
                                    color={getStatusColor(identity.status)}
                                    icon={getStatusIcon(identity.status)}
                                    size="small"
                                />
                            </Box>

                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Tooltip title={identity.publishToFamily
                                    ? "Child tenancies will inherit this domain unless specified otherwise"
                                    : "Only this tenancy can use this domain"
                                }>
                                    <Chip
                                        label={identity.publishToFamily ? "Family Sharing Enabled" : "Private"}
                                        color={identity.publishToFamily ? "success" : "default"}
                                        size="small"
                                    />
                                </Tooltip>

                                <Tooltip title={identity.publishToFamily
                                    ? `Turning family sharing off means child tenancies will stop inheriting ${identity.domain} and will fall back to the default sending domain. Tenancies with their own custom domain are unaffected.`
                                    : `Turning family sharing on means all child tenancies that don't have their own custom domain will inherit ${identity.domain} and send from it.`
                                }>
                                    <span>
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => setFamilySharingDialogOpen(true)}
                                            disabled={isUpdating}
                                            startIcon={isUpdating ? <CircularProgress size={16} /> : null}
                                        >
                                            {identity.publishToFamily ? "Turn off Family Sharing" : "Turn on Family Sharing"}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>

                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => setDialogOpen(true)}
                                disabled={isRemoving}
                                startIcon={<WarningAmberIcon />}
                            >
                                Remove Custom Domain
                            </Button>
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                            Email:
                        </Typography>
                        <Typography variant="h6" sx={{ fontFamily: "monospace" }}>
                            {identity.emailAddress}
                        </Typography>
                    </Grid>
                </Grid>

                <Box sx={{ display: "flex", gap: 2, mb: 2, mt: 2, flexWrap: "wrap" }}>
                    {!identity.isVerified && (
                        <Button
                            variant="outlined"
                            onClick={() => setShowNsRecords(!showNsRecords)}
                        >
                            {showNsRecords ? "Hide" : "See"} NS Records
                        </Button>
                    )}

                    {!identity.isVerified && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={copiedIndex === 0 ? <CheckCircleIcon /> : <ContentCopyIcon />}
                            onClick={() => handleCopyNsRecords(0)}
                        >
                            {copiedIndex === 0 ? "Copied!" : "Copy NS Records"}
                        </Button>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleCheckVerification}
                        disabled={isCheckingVerification}
                        startIcon={isCheckingVerification ? <CircularProgress size={20} /> : null}
                    >
                        {isCheckingVerification ? "Checking..." : "Check Verification"}
                    </Button>
                </Box>

                <Collapse
                    in={showNsRecords && !identity.isVerified}
                    sx={{ mb: 10 }}
                >
                    <Box sx={{
                        border: `1px solid ${lightMode ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
                        p: 2,
                        borderRadius: 1,
                        mb: 2
                    }}>
                        <Typography variant="h6" sx={{ mb: 1.5 }}>
                            NS Records Configuration Required
                        </Typography>

                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                            Please add these NS records to {identity.nsRecords?.[0]?.name?.split('.').slice(1).join('.') || identity.domain} in your DNS provider:
                        </Typography>

                        <Box sx={{ bgcolor: lightMode ? "grey.100" : "grey.800", p: 1.5, borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Record Type:</strong> NS
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Hostname:</strong> {identity.nsRecords?.[0]?.name}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Nameservers:</strong>
                            </Typography>
                            <Box component="ul" sx={{ mt: 0.5, pl: 3, mb: 0.5 }}>
                                {identity.nsRecords?.[0]?.values?.map((ns, idx) => (
                                    <li key={idx}>
                                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                            {ns}
                                        </Typography>
                                    </li>
                                ))}
                            </Box>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                <strong>TTL:</strong> {identity.nsRecords?.[0]?.ttl || 1800} (30 minutes)
                            </Typography>
                        </Box>
                    </Box>
                </Collapse>
            </MainCard>

            <ConfirmationDialog
                open={dialogOpen}
                title="Remove Custom Domain?"
                content="This will permanently delete your custom domain configuration. All DNS records will be removed from AWS. You will need to set it up again from scratch if you want to use this feature in the future."
                onConfirm={handleRemoveIdentity}
                onCancel={() => setDialogOpen(false)}
            />

            <ConfirmationDialog
                open={familySharingDialogOpen}
                title={identity.publishToFamily ? "Turn off Family Sharing?" : "Turn on Family Sharing?"}
                content={identity.publishToFamily
                    ? `Child tenancies will stop inheriting ${identity.domain} and will fall back to the default sending domain. Tenancies that have set up their own custom domain are unaffected. Are you sure you want to proceed?`
                    : `All child tenancies that don't have their own custom domain will inherit ${identity.domain} and send from it. Are you sure you want to proceed?`
                }
                onConfirm={handleToggleFamilySharing}
                onCancel={() => setFamilySharingDialogOpen(false)}
            />

            {/* Snackbar for notifications */}
            <Snackbar
                open={alert.show}
                autoHideDuration={6000}
                onClose={() => setAlert({ show: false, message: "", severity: "info" })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setAlert({ show: false, message: "", severity: "info" })}
                    severity={alert.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </>
    );
}